import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { 
  Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw, 
  Clock, Layers, Zap, Target, TrendingUp, TrendingDown, 
  Brain, Search, MessageCircle, ArrowRight
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const COLORS = {
  primary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  muted: '#6b7280',
  layers: ['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#84cc16', '#f59e0b', '#ef4444', '#ec4899', '#6366f1']
};

/**
 * AdminTelemetryDashboard - Agent Performance Monitoring Dashboard
 * 
 * Features:
 * - Summary metrics (sessions, success rate, avg duration)
 * - Timeline visualization of agent performance
 * - Error breakdown analysis
 * - Fallback layer performance metrics
 * - Recent failures debugging panel
 * - Search priority usage tracking
 * - Intent classification analytics
 */
export default function AdminTelemetryDashboard() {
  const [days, setDays] = useState('7');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [errorBreakdown, setErrorBreakdown] = useState({ total_errors: 0, breakdown: [] });
  const [layerPerformance, setLayerPerformance] = useState({ layers: [] });
  const [recentFailures, setRecentFailures] = useState({ count: 0, failures: [] });
  const [searchPriority, setSearchPriority] = useState({ sources: [] });
  const [intentStats, setIntentStats] = useState({ total_classifications: 0, intents: [] });

  useEffect(() => {
    loadTelemetryData();
  }, [days]);

  const loadTelemetryData = async () => {
    setLoading(true);
    try {
      const [
        summaryRes,
        timelineRes,
        errorsRes,
        layersRes,
        failuresRes,
        searchRes,
        intentsRes
      ] = await Promise.all([
        api.get(`/telemetry/summary?days=${days}`),
        api.get(`/telemetry/timeline?days=${days}&granularity=day`),
        api.get(`/telemetry/error-breakdown?days=${days}`),
        api.get(`/telemetry/layer-performance?days=${days}`),
        api.get('/telemetry/recent-failures?limit=20'),
        api.get(`/telemetry/search-priority-usage?days=${days}`),
        api.get(`/telemetry/intent-classification?days=${days}`)
      ]);

      setSummary(summaryRes.data);
      setTimeline(timelineRes.data?.data || []);
      setErrorBreakdown(errorsRes.data);
      setLayerPerformance(layersRes.data);
      setRecentFailures(failuresRes.data);
      setSearchPriority(searchRes.data);
      setIntentStats(intentsRes.data);
    } catch (error) {
      console.error('Failed to load telemetry data:', error);
      toast.error('Failed to load telemetry data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTelemetryData();
    setRefreshing(false);
    toast.success('Telemetry data refreshed');
  };

  // Format duration in human-readable form
  const formatDuration = (ms) => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Get trend indicator
  const getTrendBadge = (rate, threshold = 80) => {
    if (rate >= threshold) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Healthy</Badge>;
    } else if (rate >= threshold - 20) {
      return <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3 mr-1" />Warning</Badge>;
    }
    return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Critical</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-4" />
          <p className="text-gray-500">Loading telemetry data...</p>
        </div>
      </div>
    );
  }

  const hasData = summary && summary.total_sessions > 0;

  return (
    <div className="space-y-6" data-testid="admin-telemetry-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-600" />
            Agent Telemetry Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Monitor DealShaq Shopping Assistant (DSA) performance, fallback patterns, and reliability</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36" data-testid="days-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {!hasData && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Telemetry Data Yet</h3>
            <p className="text-gray-500 text-center max-w-md">
              Telemetry data will appear here once the DealShaq Shopping Assistant (DSA) processes requests. 
              Start using the agent to generate performance metrics.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {hasData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="total-sessions-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Total Sessions</p>
                    <p className="text-3xl font-bold text-gray-900">{summary.total_sessions.toLocaleString()}</p>
                    <p className="text-xs text-gray-400 mt-1">Last {days} days</p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Activity className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="success-rate-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Success Rate</p>
                    <p className="text-3xl font-bold text-gray-900">{summary.success_rate}%</p>
                    <div className="mt-1">{getTrendBadge(summary.success_rate)}</div>
                  </div>
                  <div className={`p-3 rounded-full ${summary.success_rate >= 80 ? 'bg-green-100' : summary.success_rate >= 60 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                    {summary.success_rate >= 80 ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : summary.success_rate >= 60 ? (
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="avg-duration-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Avg Response Time</p>
                    <p className="text-3xl font-bold text-gray-900">{formatDuration(summary.avg_duration_ms)}</p>
                    <p className="text-xs text-gray-400 mt-1">Per session</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="fallback-rate-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Final Fallback Rate</p>
                    <p className="text-3xl font-bold text-gray-900">{summary.fallback_rates?.final_fallback_rate || 0}%</p>
                    <div className="mt-1">
                      {summary.fallback_rates?.final_fallback_rate <= 5 ? (
                        <Badge className="bg-green-100 text-green-800">Low</Badge>
                      ) : summary.fallback_rates?.final_fallback_rate <= 15 ? (
                        <Badge className="bg-yellow-100 text-yellow-800">Moderate</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">High</Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-full">
                    <Layers className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for detailed views */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 w-full max-w-2xl">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="layers" data-testid="tab-layers">Layers</TabsTrigger>
              <TabsTrigger value="errors" data-testid="tab-errors">Errors</TabsTrigger>
              <TabsTrigger value="search" data-testid="tab-search">Search</TabsTrigger>
              <TabsTrigger value="failures" data-testid="tab-failures">Failures</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Outcome Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Outcome Distribution
                    </CardTitle>
                    <CardDescription>Session outcomes breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Success', value: summary.outcome_breakdown?.success || 0, color: COLORS.success },
                              { name: 'Failure', value: summary.outcome_breakdown?.failure || 0, color: COLORS.error },
                              { name: 'Timeout', value: summary.outcome_breakdown?.timeout || 0, color: COLORS.warning }
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {[
                              { name: 'Success', value: summary.outcome_breakdown?.success || 0, color: COLORS.success },
                              { name: 'Failure', value: summary.outcome_breakdown?.failure || 0, color: COLORS.error },
                              { name: 'Timeout', value: summary.outcome_breakdown?.timeout || 0, color: COLORS.warning }
                            ].filter(d => d.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Layers className="w-5 h-5" />
                      Fallback Layer Usage
                    </CardTitle>
                    <CardDescription>How often each fallback layer is triggered</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Auto Repair</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{ width: `${Math.min(summary.fallback_rates?.repair_rate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{summary.fallback_rates?.repair_rate || 0}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Secondary LLM</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 rounded-full" 
                              style={{ width: `${Math.min(summary.fallback_rates?.secondary_llm_rate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{summary.fallback_rates?.secondary_llm_rate || 0}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Clarifying Questions</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-cyan-500 rounded-full" 
                              style={{ width: `${Math.min(summary.fallback_rates?.clarifying_question_rate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{summary.fallback_rates?.clarifying_question_rate || 0}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Final Fallback</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500 rounded-full" 
                              style={{ width: `${Math.min(summary.fallback_rates?.final_fallback_rate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{summary.fallback_rates?.final_fallback_rate || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline Chart */}
              {timeline.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Performance Timeline
                    </CardTitle>
                    <CardDescription>Success rate and session count over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timeline}>
                          <defs>
                            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="timestamp" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            }}
                          />
                          <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                            formatter={(value, name) => [
                              name === 'Sessions' ? value : `${value}%`,
                              name
                            ]}
                          />
                          <Legend />
                          <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="sessions"
                            name="Sessions"
                            stroke={COLORS.primary}
                            fill="url(#colorSessions)"
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="success_rate"
                            name="Success Rate %"
                            stroke={COLORS.success}
                            strokeWidth={2}
                            dot={false}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Intent Classification Stats */}
              {intentStats.intents.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5" />
                      Intent Classification
                    </CardTitle>
                    <CardDescription>Distribution of user intent types</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={intentStats.intents.slice(0, 10)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="intent" type="category" width={150} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill={COLORS.info} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Layers Tab */}
            <TabsContent value="layers" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Fallback Layer Performance</CardTitle>
                  <CardDescription>Success rates and response times for each layer in the fallback cascade</CardDescription>
                </CardHeader>
                <CardContent>
                  {layerPerformance.layers.length > 0 ? (
                    <div className="space-y-4">
                      {layerPerformance.layers.map((layer, index) => (
                        <div 
                          key={layer.layer} 
                          className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                          data-testid={`layer-${layer.layer}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                style={{ backgroundColor: COLORS.layers[index % COLORS.layers.length] }}
                              >
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">{layer.layer.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                                <p className="text-sm text-gray-500">{layer.invocations.toLocaleString()} invocations</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <div className="text-center">
                                <p className="text-green-600 font-semibold">{layer.success_rate}%</p>
                                <p className="text-gray-400">success</p>
                              </div>
                              <div className="text-center">
                                <p className="text-gray-600 font-semibold">{formatDuration(layer.avg_duration_ms)}</p>
                                <p className="text-gray-400">avg time</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <div className="flex-1 h-2 bg-green-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500" 
                                style={{ width: `${layer.success_rate}%` }}
                              />
                            </div>
                            <div className="flex-1 h-2 bg-red-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-red-500" 
                                style={{ width: `${layer.failure_rate}%` }}
                              />
                            </div>
                            <div className="flex-1 h-2 bg-yellow-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-yellow-500" 
                                style={{ width: `${layer.timeout_rate}%` }}
                              />
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 mt-1">
                            <span>Success: {layer.success_rate}%</span>
                            <span>Failure: {layer.failure_rate}%</span>
                            <span>Timeout: {layer.timeout_rate}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No layer performance data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Errors Tab */}
            <TabsContent value="errors" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Error Type Breakdown
                  </CardTitle>
                  <CardDescription>Most common error types in the last {days} days</CardDescription>
                </CardHeader>
                <CardContent>
                  {errorBreakdown.breakdown.length > 0 ? (
                    <div className="space-y-4">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={errorBreakdown.breakdown}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="count"
                              nameKey="error_type"
                              label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                            >
                              {errorBreakdown.breakdown.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS.layers[index % COLORS.layers.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-3">Error Details</h4>
                        <div className="space-y-2">
                          {errorBreakdown.breakdown.map((error, index) => (
                            <div 
                              key={error.error_type} 
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: COLORS.layers[index % COLORS.layers.length] }}
                                />
                                <div>
                                  <p className="font-medium text-gray-900">{error.error_type}</p>
                                  {error.sample_message && (
                                    <p className="text-xs text-gray-500 truncate max-w-md">{error.sample_message}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">{error.count}</p>
                                <p className="text-xs text-gray-500">{error.percentage}%</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                      <p>No errors recorded in this period!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Search Tab */}
            <TabsContent value="search" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Search Priority Usage
                  </CardTitle>
                  <CardDescription>How often searches expand beyond preferred stores</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={searchPriority.sources}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="source" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="searches" name="Searches" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="avg_items_found" name="Avg Items Found" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {searchPriority.sources.map((source) => (
                      <div key={source.source} className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">{source.source}</p>
                        <p className="text-xl font-bold text-gray-900">{source.searches}</p>
                        <p className="text-xs text-gray-400">searches</p>
                        <p className="text-sm text-green-600 mt-1">{source.avg_fulfillment_rate}% fulfilled</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Failures Tab */}
            <TabsContent value="failures" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    Recent Failures ({recentFailures.count})
                  </CardTitle>
                  <CardDescription>Most recent failed sessions for debugging</CardDescription>
                </CardHeader>
                <CardContent>
                  {recentFailures.failures.length > 0 ? (
                    <div className="space-y-3">
                      {recentFailures.failures.map((failure) => (
                        <div 
                          key={failure.session_id} 
                          className="p-4 border rounded-lg hover:border-red-200 transition-colors"
                          data-testid={`failure-${failure.session_id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant="outline" 
                                  className={failure.final_outcome === 'timeout' ? 'border-yellow-500 text-yellow-700' : 'border-red-500 text-red-700'}
                                >
                                  {failure.final_outcome}
                                </Badge>
                                {failure.schema_type && (
                                  <Badge variant="outline">{failure.schema_type}</Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-1 font-mono">{failure.session_id}</p>
                            </div>
                            <div className="text-right text-sm">
                              <p className="text-gray-500">
                                {failure.started_at ? new Date(failure.started_at).toLocaleString() : 'N/A'}
                              </p>
                              <p className="text-gray-400">{formatDuration(failure.total_duration_ms)}</p>
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded mt-2">
                            <p className="text-sm text-gray-600 mb-1 font-medium">User Message:</p>
                            <p className="text-sm text-gray-800">{failure.user_message || 'N/A'}</p>
                          </div>
                          {failure.error_summary && (
                            <div className="mt-2 p-2 bg-red-50 rounded border border-red-100">
                              <p className="text-xs text-red-700 font-mono">{failure.error_summary}</p>
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                            <span>Layer: {failure.final_layer || 'N/A'}</span>
                            <span>Primary attempts: {failure.primary_attempts}</span>
                            <span>Repair attempts: {failure.repair_attempts}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                      <p>No recent failures!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
